import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import AdminModal from '../../components/AdminModal';
import SearchSelect from '../../components/SearchSelect';
import { apiError, inputClasses, invalidateInitiativeQueries, labelClasses } from './InitiativeDetailPage';

export interface Proponent {
  id: string;
  person_id: string | null;
  person_name: string | null;
  name: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  display_name: string | null;
  display_email: string | null;
  display_phone: string | null;
}

interface Props {
  initiativeId: string;
  proponents: Proponent[];
}

const EMPTY = { person_id: '', name: '', email: '', phone: '', notes: '' };

/**
 * Designated representatives on the filing.
 *
 * Person-or-free-text, the same shape as endorsements: link to the registry when
 * they are in it, type a name when they are not. Contact details here are public
 * record from the SOS filing — personal addresses are deliberately not stored.
 */
const InitiativeProponentsCard: React.FC<Props> = ({ initiativeId, proponents }) => {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Proponent | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [picked, setPicked] = useState<{ id: string; label: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Proponent | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => invalidateInitiativeQueries(queryClient, initiativeId);

  const searchPeople = async (term: string) => {
    const response = await api.get('/admin/people', { params: { search: term, limit: 10 } });
    return (response.data.data ?? []).map((person: any) => ({
      id: person.id,
      label: person.full_name,
      sublabel: person.primary_email ?? undefined,
    }));
  };

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY);
    setPicked(null);
    setError(null);
    setModalOpen(true);
  };

  const openEdit = (proponent: Proponent) => {
    setEditing(proponent);
    setForm({
      person_id: proponent.person_id ?? '',
      name: proponent.name ?? '',
      email: proponent.email ?? '',
      phone: proponent.phone ?? '',
      notes: proponent.notes ?? '',
    });
    setPicked(
      proponent.person_id
        ? { id: proponent.person_id, label: proponent.person_name ?? 'Linked person' }
        : null
    );
    setError(null);
    setModalOpen(true);
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const body = {
        person_id: form.person_id || null,
        name: form.name.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        notes: form.notes.trim() || null,
      };
      return editing
        ? api.put(`/admin/ballot-initiatives/${initiativeId}/proponents/${editing.id}`, body)
        : api.post(`/admin/ballot-initiatives/${initiativeId}/proponents`, body);
    },
    onSuccess: () => {
      toast.success(editing ? 'Proponent updated' : 'Proponent added');
      setModalOpen(false);
      refresh();
    },
    onError: (err: any) => setError(apiError(err, 'Failed to save the proponent')),
  });

  const deleteMutation = useMutation({
    mutationFn: (proponent: Proponent) =>
      api.delete(`/admin/ballot-initiatives/${initiativeId}/proponents/${proponent.id}`),
    onSuccess: () => {
      toast.success('Proponent removed');
      setConfirmDelete(null);
      refresh();
    },
    onError: (err: any) => toast.error(apiError(err, 'Failed to remove the proponent')),
  });

  // Mirrors the DB CHECK: a row that identifies nobody says nothing.
  const saveDisabled = (!form.person_id && !form.name.trim()) || saveMutation.isPending;

  return (
    <div className="card p-5">
      <div className="mb-4 flex items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-text-primary dark:text-text-dark-primary">
          Proponents{proponents.length > 0 && ` (${proponents.length})`}
        </h3>
        <Button variant="ghost" size="sm" icon={Plus} onClick={openAdd}>
          Add Proponent
        </Button>
      </div>

      {proponents.length === 0 ? (
        <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
          No designated representatives recorded.
        </p>
      ) : (
        <ul className="space-y-2.5">
          {proponents.map((proponent) => (
            <li key={proponent.id} className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm text-text-primary dark:text-text-dark-primary">
                  {proponent.display_name || '—'}
                  {proponent.person_id && (
                    <span className="ml-1.5 text-xs text-text-tertiary dark:text-text-dark-tertiary">linked</span>
                  )}
                </p>
                {(proponent.display_email || proponent.display_phone) && (
                  <p className="text-xs text-text-secondary dark:text-text-dark-secondary">
                    {[proponent.display_email, proponent.display_phone].filter(Boolean).join(' · ')}
                  </p>
                )}
                {proponent.notes && (
                  <p className="mt-0.5 text-xs text-text-tertiary dark:text-text-dark-tertiary">{proponent.notes}</p>
                )}
              </div>
              <div className="flex shrink-0 gap-1">
                <Button variant="ghost" size="sm" icon={Pencil} ariaLabel="Edit proponent" onClick={() => openEdit(proponent)} />
                <Button variant="ghost" size="sm" icon={Trash2} ariaLabel="Remove proponent" onClick={() => setConfirmDelete(proponent)} />
              </div>
            </li>
          ))}
        </ul>
      )}

      <AdminModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit Proponent' : 'Add Proponent'}
        footer={<>
          <Button variant="secondary" size="sm" onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={() => saveMutation.mutate()} disabled={saveDisabled}>
            {saveMutation.isPending ? "Saving…" : "Save"}
          </Button>
        </>}
      >
        <div className="space-y-4">
          {error && (
            <p className="rounded-lg bg-danger-light px-3 py-2 text-sm text-danger dark:bg-danger-dark/20">{error}</p>
          )}
          <div>
            <span className={labelClasses}>Person</span>
            <SearchSelect
              placeholder="Search the person registry…"
              ariaLabel="Link a person"
              fetchOptions={searchPeople}
              selected={picked}
              onSelect={(option) => {
                setPicked(option);
                setForm((c) => ({ ...c, person_id: option.id }));
              }}
              onClear={() => {
                setPicked(null);
                setForm((c) => ({ ...c, person_id: '' }));
              }}
            />
            <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
              Leave empty and type a name below for someone not yet in the registry.
            </p>
          </div>
          <div>
            <label htmlFor="proponent-name" className={labelClasses}>Name</label>
            <input
              id="proponent-name"
              type="text"
              value={form.name}
              onChange={(e) => setForm((c) => ({ ...c, name: e.target.value }))}
              className={inputClasses}
            />
          </div>
          <div>
            <label htmlFor="proponent-email" className={labelClasses}>Email</label>
            <input
              id="proponent-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((c) => ({ ...c, email: e.target.value }))}
              className={inputClasses}
            />
          </div>
          <div>
            <label htmlFor="proponent-phone" className={labelClasses}>Phone</label>
            <input
              id="proponent-phone"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm((c) => ({ ...c, phone: e.target.value }))}
              className={inputClasses}
            />
          </div>
          <div>
            <label htmlFor="proponent-notes" className={labelClasses}>Notes</label>
            <textarea
              id="proponent-notes"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm((c) => ({ ...c, notes: e.target.value }))}
              className={inputClasses}
            />
          </div>
        </div>
      </AdminModal>

      <ConfirmationDialog
        isOpen={confirmDelete !== null}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && deleteMutation.mutate(confirmDelete)}
        title="Remove this proponent?"
        message={confirmDelete ? `${confirmDelete.display_name ?? 'This proponent'} will be removed from the measure.` : ''}
        confirmText="Remove"
        variant="danger"
      />
    </div>
  );
};

export default InitiativeProponentsCard;
